/**
 * MODULE: _shared/frontend-shell/src/features/crm/components/accounts/AccountCustomerHealthBadge.tsx
 * PURPOSE: Composite customer health score with expandable breakdown (customer health)
 * PUBLIC_API: AccountCustomerHealthBadge
 */

import { ChevronDown, ChevronUp, HeartPulse } from 'lucide-react';
import { useState, type ReactElement } from 'react';
import { twMerge } from 'tailwind-merge';
import { useCrmAccountHealth } from '../../hooks/useCrmAccountHealth';
import {
  customerHealthLabel,
  customerHealthPillClass,
  customerHealthRingClass,
  customerHealthBarClass,
} from '../../utils/customerHealthLabel';

export type AccountCustomerHealthBadgeProps = {
  accountId: string;
};

function ScoreRing({ score }: { score: number }) {
  const pct = Math.max(0, Math.min(100, score));
  const r = 18;
  const c = 2 * Math.PI * r;
  const offset = c - (pct / 100) * c;
  return (
    <svg width="44" height="44" viewBox="0 0 44 44" aria-hidden className="-rotate-90">
      <circle cx="22" cy="22" r={r} fill="none" stroke="currentColor" strokeWidth="4" className="text-gray-200 dark:text-gray-700" />
      <circle
        cx="22"
        cy="22"
        r={r}
        fill="none"
        stroke="currentColor"
        strokeWidth="4"
        strokeLinecap="round"
        strokeDasharray={c}
        strokeDashoffset={offset}
        className={customerHealthRingClass(score)}
      />
    </svg>
  );
}

export function AccountCustomerHealthBadge({ accountId }: AccountCustomerHealthBadgeProps): ReactElement {
  const { health, loading } = useCrmAccountHealth(accountId);
  const [expanded, setExpanded] = useState(false);

  if (loading && !health) {
    return (
      <p className="text-xs text-gray-500 dark:text-gray-400" data-testid="account-health-loading">
        Calculating health…
      </p>
    );
  }
  if (!health) return <></>;

  const si = health.salesIntelligenceScore;

  return (
    <div className="w-full" data-testid="account-customer-health">
      <button
        type="button"
        className="flex w-full items-center gap-3 rounded-lg border border-gray-200 bg-gray-50/80 px-3 py-2 text-left hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-900/40 dark:hover:bg-gray-900/60"
        data-testid="account-health-toggle"
        aria-expanded={expanded}
        onClick={() => setExpanded((v) => !v)}
      >
        <div className="relative flex h-11 w-11 shrink-0 items-center justify-center">
          <ScoreRing score={health.score} />
          <span className="absolute text-sm font-bold tabular-nums text-gray-900 dark:text-white">{health.score}</span>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Customer health
            </span>
            <span
              className={twMerge(
                'rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1 ring-inset',
                customerHealthPillClass(health.tier)
              )}
              data-testid="account-health-tier"
            >
              {customerHealthLabel(health.tier)}
            </span>
          </div>
          <p className="mt-0.5 text-xs text-gray-600 dark:text-gray-400">
            Weighted from activity, products, contracts, and pipeline
            {si != null ? ` · SI lead score ${si} shown separately` : ''}
          </p>
        </div>
        {expanded ? (
          <ChevronUp className="h-4 w-4 shrink-0 text-gray-400" aria-hidden />
        ) : (
          <ChevronDown className="h-4 w-4 shrink-0 text-gray-400" aria-hidden />
        )}
      </button>

      {expanded ? (
        <ul className="mt-2 space-y-2 rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-600 dark:bg-gray-900/30" data-testid="account-health-breakdown">
          {health.components.map((c) => (
            <li key={c.id}>
              <div className="flex items-center justify-between gap-2 text-xs">
                <span className="font-medium text-gray-800 dark:text-gray-200">{c.label}</span>
                <span className="tabular-nums font-semibold text-gray-900 dark:text-white">{c.score}</span>
              </div>
              <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
                <div
                  className={twMerge('h-full rounded-full', customerHealthBarClass(c.score))}
                  style={{ width: `${Math.max(0, Math.min(100, c.score))}%` }}
                />
              </div>
              <p className="mt-1 text-[10px] text-gray-500 dark:text-gray-400">{c.detail}</p>
            </li>
          ))}
          {si != null ? (
            <li className="flex items-center gap-2 border-t border-gray-100 pt-2 text-[10px] text-gray-500 dark:border-gray-700 dark:text-gray-400">
              <HeartPulse className="h-3.5 w-3.5 shrink-0" aria-hidden />
              Sales Intelligence lead score: <span className="font-semibold tabular-nums text-gray-800 dark:text-gray-200">{si}</span>
              {' '}(not included in customer health)
            </li>
          ) : null}
        </ul>
      ) : null}
    </div>
  );
}
